// Mouse-wheel and pan use raw `canvas.on('mouse:*', ...)` because the editor
// bus does not expose pointer-level events. Documented exception per criteria
// doc, section "Centralized typed event bus".
import type { fabric } from 'fabric';
import type { FabricEditor } from '../core/fabric-editor';
import type { EditorPlugin } from '../interfaces/editor-plugin';
import { FabricPluginName } from './plugin-name';

export interface ZoomFacade {
  get(): number;
  set(value: number): void;
  in(step?: number): void;
  out(step?: number): void;
  reset(): void;
}

declare module '../core/fabric-editor' {
  interface FabricEditor {
    zoom?: ZoomFacade;
  }
}

export interface ZoomPluginOptions {
  min?: number;
  max?: number;
  enablePan?: boolean;
  panModifier?: 'alt' | 'meta';
}

export function zoomPlugin(options: ZoomPluginOptions = {}): EditorPlugin {
  const min = options.min ?? 0.01;
  const max = options.max ?? 20;
  const enablePan = options.enablePan ?? true;
  const panModifier = options.panModifier ?? (isMac() ? 'meta' : 'alt');

  return {
    name: FabricPluginName.ZOOM,
    install(editor: FabricEditor) {
      const canvas = editor.canvas;

      const clamp = (z: number) => Math.min(max, Math.max(min, z));

      const setZoom = (next: number, point?: { x: number; y: number }) => {
        const zoom = clamp(next);
        const target = point ?? { x: canvas.getWidth() / 2, y: canvas.getHeight() / 2 };
        canvas.zoomToPoint(target as fabric.Point, zoom);
        canvas.requestRenderAll();
        editor.emit('zoom:changed', { zoom });
      };

      const onWheel = (opt: fabric.IEvent<WheelEvent>) => {
        const e = opt.e;
        const current = canvas.getZoom();
        const next = current * 0.999 ** e.deltaY;
        setZoom(next, { x: e.offsetX, y: e.offsetY });
        e.preventDefault();
        e.stopPropagation();
      };

      let dragging = false;
      let lastX = 0;
      let lastY = 0;

      const onDown = (opt: fabric.IEvent<MouseEvent>) => {
        if (!enablePan) return;
        const e = opt.e;
        const active = panModifier === 'meta' ? e.metaKey : e.altKey;
        if (!active) return;
        dragging = true;
        canvas.selection = false;
        lastX = e.clientX;
        lastY = e.clientY;
      };

      const onMove = (opt: fabric.IEvent<MouseEvent>) => {
        if (!dragging) return;
        const e = opt.e;
        const vpt = canvas.viewportTransform;
        if (!vpt) return;
        vpt[4] += e.clientX - lastX;
        vpt[5] += e.clientY - lastY;
        canvas.requestRenderAll();
        lastX = e.clientX;
        lastY = e.clientY;
      };

      const onUp = () => {
        if (!dragging) return;
        dragging = false;
        canvas.selection = true;
      };

      canvas.on('mouse:wheel', onWheel);
      canvas.on('mouse:down', onDown);
      canvas.on('mouse:move', onMove);
      canvas.on('mouse:up', onUp);

      const facade: ZoomFacade = {
        get: () => canvas.getZoom(),
        set: (value) => setZoom(value),
        in: (step = 1.1) => setZoom(canvas.getZoom() * step),
        out: (step = 1.1) => setZoom(canvas.getZoom() / step),
        reset: () => {
          if (editor.autoFit) editor.fit();
          else setZoom(1);
        },
      };
      editor.zoom = facade;

      (facade as ZoomFacade & { __off?: () => void }).__off = () => {
        canvas.off('mouse:wheel', onWheel as never);
        canvas.off('mouse:down', onDown as never);
        canvas.off('mouse:move', onMove as never);
        canvas.off('mouse:up', onUp as never);
      };
    },
    dispose(editor: FabricEditor) {
      const facade = editor.zoom as (ZoomFacade & { __off?: () => void }) | undefined;
      facade?.__off?.();
      editor.zoom = undefined;
    },
  };
}

function isMac(): boolean {
  if (typeof navigator === 'undefined') return false;
  return navigator.userAgent.toLowerCase().includes('mac');
}
